import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Building2, Sparkles, Pencil, Trash2, PauseCircle, PlayCircle, ShieldCheck, ShieldOff, ShieldAlert, AlertTriangle, Settings, Receipt } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("it-IT") : "—";

const STATO_STYLE = {
  attiva:   { c: "bg-emerald-100 text-emerald-800", i: ShieldCheck, l: "Attiva" },
  sospesa:  { c: "bg-amber-100 text-amber-800",     i: ShieldAlert, l: "Sospesa" },
  revocata: { c: "bg-slate-200 text-slate-600",      i: ShieldOff,   l: "Revocata" },
};

/** Iter122ch · FASE 2 · Sub-licenze per condominio (pannello dentro card studio). */
export default function SuperAdminSubLicenze({ tenant, piani = [] }) {
  const [items, setItems] = useState([]);
  const [riep, setRiep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [openEdit, setOpenEdit] = useState(null); // sub-licenza selezionata
  const [bulk, setBulk] = useState({ piano_id: "", ciclo_billing: "annuale", attivazione_at: new Date().toISOString().slice(0,10) });
  const [editForm, setEditForm] = useState({ prezzo_annuo: "", scadenza_at: "", note: "" });

  const [preflight, setPreflight] = useState(null);
  const [modalita, setModalita] = useState(tenant.modalita_fatturazione || "studio");

  const load = () => api.get(`/superadmin/tenants/${tenant.id}/licenze-condominio`)
    .then((r) => { setItems(r.data.items || []); setRiep(r.data.riepilogo || null); })
    .catch(() => {});
  const loadPreflight = () => api.get(`/superadmin/tenants/${tenant.id}/licenze-condominio/preflight`)
    .then((r) => setPreflight(r.data))
    .catch(() => {});
  useEffect(() => { load(); loadPreflight(); /* eslint-disable-next-line */ }, [tenant.id]);

  const cambiaModalita = async (nuova) => {
    if (nuova === modalita) return;
    if (nuova === "condominio" && preflight && !preflight.pronto) {
      const dett = [];
      if (preflight.senza_licenza.length) dett.push(`${preflight.senza_licenza.length} condomìni senza sub-licenza`);
      if (preflight.senza_dati_fatturazione.length) dett.push(`${preflight.senza_dati_fatturazione.length} senza dati fatturazione`);
      if (!window.confirm(`ATTENZIONE: lo studio non è pronto per la modalità 'per condominio'.\n\n${dett.join("\n")}\n\nProcedere comunque? (Le pro-forma potrebbero fallire per i condomìni non pronti)`)) return;
    }
    if (!window.confirm(`Impostare il default di aggregazione pro-forma a "${nuova === "studio" ? "Aggregata allo Studio (1 pro-forma)" : "Una per Condominio (N pro-forma)"}"?`)) return;
    try {
      await api.put(`/superadmin/tenants/${tenant.id}`, { modalita_fatturazione: nuova });
      setModalita(nuova);
      toast.success("Default aggregazione pro-forma aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const runBulk = async () => {
    if (!bulk.piano_id) { toast.error("Seleziona un piano"); return; }
    if (!window.confirm("Creare le sub-licenze per tutti i condomìni dello studio? I condomìni già coperti verranno saltati.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/tenants/${tenant.id}/licenze-condominio/bulk-create`, bulk);
      toast.success(`Sub-licenze: ${data.created} create · ${data.skipped} saltate${data.errori.length ? ` · ${data.errori.length} errori` : ""}`);
      setOpenBulk(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore bulk-create"); }
    setBusy(false);
  };

  const setStato = async (l, stato) => {
    const label = stato === "sospesa" ? "Sospendere" : stato === "attiva" ? "Riattivare" : "Revocare";
    if (!window.confirm(`${label} la sub-licenza per ${l.condominio_snapshot?.nome}?`)) return;
    try {
      await api.put(`/superadmin/tenants/${tenant.id}/licenze-condominio/${l.id}`, { stato });
      toast.success(`Sub-licenza aggiornata: ${stato}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const revoca = async (l) => {
    if (!window.confirm(`Revocare definitivamente la sub-licenza di ${l.condominio_snapshot?.nome}?`)) return;
    try {
      await api.delete(`/superadmin/tenants/${tenant.id}/licenze-condominio/${l.id}`);
      toast.success("Sub-licenza revocata"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const emettiPerSubLic = async (l) => {
    if (!window.confirm(`Emettere pro-forma per il condominio ${l.condominio_snapshot?.nome}?`)) return;
    try {
      const { data } = await api.post(`/superadmin/tenants/${tenant.id}/licenze-condominio/${l.id}/proforma`, { note: "" });
      toast.success(`Emessa ${data.numero} · netto €${(data.netto_a_pagare || 0).toFixed(2)}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore emissione"); }
  };

  const emettiBulk = async () => {
    if (!window.confirm(`Emettere una pro-forma per OGNI sub-licenza attiva dello studio?`)) return;
    try {
      const { data } = await api.post(`/superadmin/tenants/${tenant.id}/proforma-bulk`, { note: "" });
      const msg = `Emesse ${data.emesse} pro-forma${data.errori.length ? ` · ${data.errori.length} errori` : ""}`;
      data.emesse > 0 ? toast.success(msg) : toast.error(msg);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const apriEdit = (l) => {
    setOpenEdit(l);
    setEditForm({ prezzo_annuo: l.prezzo_annuo, scadenza_at: l.scadenza_at || "", note: l.note || "" });
  };
  const salvaEdit = async () => {
    setBusy(true);
    try {
      const body = {};
      if (editForm.prezzo_annuo !== "" && editForm.prezzo_annuo != null) body.prezzo_annuo = Number(editForm.prezzo_annuo);
      if (editForm.scadenza_at) body.scadenza_at = editForm.scadenza_at;
      if (editForm.note !== openEdit.note) body.note = editForm.note;
      await api.put(`/superadmin/tenants/${tenant.id}/licenze-condominio/${openEdit.id}`, body);
      toast.success("Sub-licenza aggiornata"); setOpenEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid={`sublicenze-${tenant.id}`}>
      {/* Iter122ci · Toggle default aggregazione pro-forma · Iter122em · rinominato per chiarezza
          (la scelta di intestazione fattura è dell'admin tenant per singolo condominio al momento
          dell'attivazione licenza; questo toggle definisce SOLO il default di aggregazione delle
          pro-forma di rinnovo emesse in blocco dallo Studio). */}
      <div className="p-3 border-2 rounded-lg bg-white space-y-2" data-testid={`modalita-fatt-${tenant.id}`}>
        <div className="flex items-center gap-2 text-sm">
          <Settings className="w-4 h-4 text-brand" />
          <b>Default aggregazione pro-forma</b>
          <span className="text-xs text-slate-500">(scelta rinnovo automatico · l&apos;intestazione fattura la sceglie l&apos;admin per singolo condominio)</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <label className={`flex items-start gap-2 p-2.5 rounded-lg border-2 cursor-pointer flex-1 ${modalita === "studio" ? "border-brand bg-brand/5" : "border-slate-200"}`}>
            <input type="radio" name={`modalita-${tenant.id}`} className="mt-1" checked={modalita === "studio"} onChange={() => cambiaModalita("studio")} data-testid={`modalita-studio-${tenant.id}`} />
            <div className="text-sm">
              <div className="font-semibold">Aggregata allo Studio</div>
              <div className="text-xs text-slate-500">1 sola pro-forma di rinnovo intestata allo studio · totale = prezzo/cond × n. condomìni con licenza attiva</div>
            </div>
          </label>
          <label className={`flex items-start gap-2 p-2.5 rounded-lg border-2 cursor-pointer flex-1 ${modalita === "condominio" ? "border-brand bg-brand/5" : "border-slate-200"}`}>
            <input type="radio" name={`modalita-${tenant.id}`} className="mt-1" checked={modalita === "condominio"} onChange={() => cambiaModalita("condominio")} data-testid={`modalita-condominio-${tenant.id}`} />
            <div className="text-sm">
              <div className="font-semibold">Una per Condominio</div>
              <div className="text-xs text-slate-500">N pro-forma di rinnovo · una per condominio con licenza attiva · richiede dati fatturazione per ognuno</div>
            </div>
          </label>
        </div>
        {modalita === "condominio" && preflight && !preflight.pronto && (
          <div className="p-2.5 bg-amber-50 border-2 border-amber-300 rounded-lg text-xs flex items-start gap-2" data-testid={`preflight-warn-${tenant.id}`}>
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900">Aggregazione "Una per Condominio" non pronta</div>
              <ul className="mt-1 list-disc pl-4 text-amber-800 space-y-0.5">
                {preflight.senza_licenza.length > 0 && (
                  <li><b>{preflight.senza_licenza.length}</b> condomìni senza sub-licenza (l&apos;admin tenant deve completare l&apos;attivazione da Impostazioni &amp; Account)</li>
                )}
                {preflight.senza_dati_fatturazione.length > 0 && (
                  <li><b>{preflight.senza_dati_fatturazione.length}</b> condomìni senza dati di fatturazione (CF o email) — l&apos;admin tenant li compili in Stabili</li>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-brand" />
          <b className="text-sm text-slate-800">Sub-licenze per condominio</b>
          {riep && (
            <span className="text-xs text-slate-500">
              · {riep.attive} attive · {riep.sospese} sospese · {riep.revocate} revocate · imponibile annuo {eur(riep.imponibile_annuo)}
            </span>
          )}
        </div>
        <Button size="sm" variant="outline" className="h-8" onClick={() => setOpenBulk(true)} data-testid={`sublicenze-bulk-${tenant.id}`}>
          <Sparkles className="w-4 h-4 mr-1" /> Bulk-create
        </Button>
        {modalita === "condominio" && (riep?.attive || 0) > 0 && (
          <Button size="sm" className="h-8 bg-brand hover:bg-brand-dark" onClick={emettiBulk}
                  data-testid={`sublicenze-emetti-tutte-${tenant.id}`}>
            <Receipt className="w-4 h-4 mr-1" /> Emetti tutte ({riep.attive})
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-slate-400 italic p-3 bg-slate-50 rounded border">
          Nessuna sub-licenza. Usa &quot;Bulk-create&quot; per generarle in automatico da tutti i condomìni dello studio.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((l) => {
            const S = STATO_STYLE[l.stato] || STATO_STYLE.attiva;
            const Ic = S.i;
            return (
              <div key={l.id} className="flex flex-wrap items-center gap-2 text-xs p-2 border rounded bg-white"
                   data-testid={`sublicenza-${l.id}`}>
                <span className={`px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1 ${S.c}`}>
                  <Ic className="w-3 h-3" /> {S.l}
                </span>
                <span className="font-semibold text-slate-800">{l.condominio_snapshot?.nome || l.condominio_id}</span>
                <span className="text-slate-500">{l.piano_nome_snapshot} · {l.ciclo_billing}</span>
                <span>{eur(l.prezzo_annuo)}/anno
                  {l.prezzo_forzato && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-bold text-[10px]">ECCEZIONE</span>
                  )}
                </span>
                <span className="text-slate-500">scad. {fmtDate(l.scadenza_at)}</span>
                <div className="flex items-center gap-1 ml-auto">
                  {l.stato === "attiva" && modalita === "condominio" && (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-brand border-brand/30 hover:bg-brand/5"
                            onClick={() => emettiPerSubLic(l)} data-testid={`sublicenza-emetti-${l.id}`}
                            title="Emetti pro-forma per questo condominio">
                      <Receipt className="w-3 h-3" />
                    </Button>
                  )}
                  {l.stato !== "revocata" && (
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => apriEdit(l)}
                            data-testid={`sublicenza-edit-${l.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                  )}
                  {l.stato === "attiva" && (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-amber-700 border-amber-300"
                            onClick={() => setStato(l, "sospesa")} data-testid={`sublicenza-sospendi-${l.id}`}>
                      <PauseCircle className="w-3 h-3" />
                    </Button>
                  )}
                  {l.stato === "sospesa" && (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-emerald-700 border-emerald-300"
                            onClick={() => setStato(l, "attiva")} data-testid={`sublicenza-riattiva-${l.id}`}>
                      <PlayCircle className="w-3 h-3" />
                    </Button>
                  )}
                  {l.stato !== "revocata" && (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-red-600 border-red-300"
                            onClick={() => revoca(l)} data-testid={`sublicenza-revoca-${l.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog Bulk-create */}
      <Dialog open={openBulk} onOpenChange={setOpenBulk}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl">Bulk-create sub-licenze</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">
              Crea una sub-licenza per ogni condominio dello studio non ancora coperto. Il prezzo di default è quello del piano.
            </div>
            <div>
              <Label className="text-sm">Piano</Label>
              <select value={bulk.piano_id} onChange={(e) => setBulk({ ...bulk, piano_id: e.target.value })}
                      data-testid="sublicenze-bulk-piano"
                      className="mt-1 h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-sm">
                <option value="">— scegli piano —</option>
                {piani.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Ciclo</Label>
                <select value={bulk.ciclo_billing} onChange={(e) => setBulk({ ...bulk, ciclo_billing: e.target.value })}
                        className="mt-1 h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white text-sm">
                  <option value="annuale">Annuale</option>
                  <option value="semestrale">Semestrale</option>
                  <option value="trimestrale">Trimestrale</option>
                  <option value="mensile">Mensile</option>
                </select>
              </div>
              <div>
                <Label className="text-sm">Attivazione</Label>
                <Input type="date" value={bulk.attivazione_at}
                       onChange={(e) => setBulk({ ...bulk, attivazione_at: e.target.value })}
                       className="mt-1 h-11" data-testid="sublicenze-bulk-attivazione" />
              </div>
            </div>
            <Button className="w-full h-11 bg-brand hover:bg-brand-dark" disabled={busy} onClick={runBulk}
                    data-testid="sublicenze-bulk-run">
              <Sparkles className="w-4 h-4 mr-1" /> Crea sub-licenze
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Edit prezzo/scadenza/note */}
      <Dialog open={!!openEdit} onOpenChange={(v) => !v && setOpenEdit(null)}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl">Modifica sub-licenza</DialogTitle></DialogHeader>
          {openEdit && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">
                {openEdit.condominio_snapshot?.nome} · piano {openEdit.piano_nome_snapshot} · listino {eur(openEdit.prezzo_listino_piano)}
              </div>
              <div>
                <Label className="text-sm">Prezzo annuo (eccezione)</Label>
                <Input type="number" step="0.01" value={editForm.prezzo_annuo}
                       onChange={(e) => setEditForm({ ...editForm, prezzo_annuo: e.target.value })}
                       className="mt-1 h-11" data-testid="sublicenze-edit-prezzo" />
                <div className="text-xs text-slate-400 mt-1">Lascia al valore listino per riportare al default</div>
              </div>
              <div>
                <Label className="text-sm">Scadenza</Label>
                <Input type="date" value={editForm.scadenza_at}
                       onChange={(e) => setEditForm({ ...editForm, scadenza_at: e.target.value })}
                       className="mt-1 h-11" data-testid="sublicenze-edit-scadenza" />
              </div>
              <div>
                <Label className="text-sm">Note</Label>
                <Input value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                       className="mt-1 h-11" />
              </div>
              <Button className="w-full h-11 bg-brand hover:bg-brand-dark" disabled={busy} onClick={salvaEdit}
                      data-testid="sublicenze-edit-save">
                Salva modifiche
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
